import React, {useEffect, useMemo, useRef} from 'react'
import useEinstein from '../../store/hooks/useEinstein'
import {useProductStore, useGlobalStore} from '../../store/hooks/useStore'
import {BoxProps, Container, Flex, Stack} from '../../vanilla'
import { ProductTile } from '../product-tile'
import {Hit, ProductModel} from '../../store/ProductStore'
import * as styles from './styles.css'
import {Carousel} from '../carousel'
import {recommenderHit, recommenderItemSpacing} from './styles.css'
import {useHitsProductBadges} from '../algolia/util/useHitsProductBadges'
import {RecommendationsParams} from 'commerce-api'
import {Spinner} from '../spinner'
import {observer} from 'mobx-react'
import { useViewItemListTracking } from '../../analytics/view-item-list-tracking'
import {ANALYTICS_CAROUSEL_NAME} from '../../constants'
import { transformHit } from '../algolia/util/transformHit'

export interface RecommenderProps {
  name?: string
  productId?: string | string[]
  recommender?: string
  categoryId?: string
  containerProps?: BoxProps<'div'>
}

const RecommenderContainer = ({
  name,
  productId,
  recommender,
  categoryId,
  containerProps
}: RecommenderProps) => {
  const einstein = useEinstein()
  const {getRecommendations, recommendations, sendViewRecommendations, recoUUID, loading} = einstein
  const {productsById, getItemPrice} = useProductStore()
  const {selectedRegion, selectedStoreId, selectedPriceDayOffset} = useGlobalStore()
  const ref = useRef(null)
  const hits = useMemo(
    () =>
      recommendations?.length
        ? (
            recommendations.map((product) =>
              productsById[product.id] ? transformHit(productsById[product.id] as unknown as Hit, selectedPriceDayOffset, selectedStoreId, selectedRegion) : null
            )
          ).filter(hit => {
            if (!hit) return false 
        
            const {displayPrice} = getItemPrice(hit as unknown as ProductModel)
        
            const isButtonDisabled = !displayPrice
              || hit.availableForDayAtStore === false
              || hit.isAddToCartDisabled === true
        
              return !isButtonDisabled
            }) as Hit[]
        : [] as Hit[],
    [recommendations, productsById]
  )

  useHitsProductBadges(hits)
  
  useEffect(() => {
    const fetchRecommendations = async () => {
      let args
      if (productId) {
        args = {
          products: Array.isArray(productId) ? productId.map((id) => ({id})) : [{id: productId}],
        }
      } else if (categoryId) {
        args = {categories: [{id: categoryId}]}
      }
      if (recommender) {
        getRecommendations(recommender, args as RecommendationsParams)
      }
    }
    fetchRecommendations().catch(console.error)
  }, [productId, categoryId])
  
  // GTM Tracking
  const sendViewItemListData = useViewItemListTracking()
    
  /* Triggers Einstein and Data Layer events, when recommendations are loaded and section is on user screen */
  useEffect(() => {
    if (recommendations.length && recoUUID && hits) {
      sendViewRecommendations({
        recommenderName: recommender || '',
        __recoUUID: recoUUID,
        products: recommendations,
      })
      const viewItemListData = {
          items: hits,
          listType: 
            {
              id: recoUUID,
              name: 'Recommendations Carousel'
            },
          pageId: null,
          region: selectedRegion
        }
      sendViewItemListData(viewItemListData)
    }
  }, [recommendations, hits])

  return (
    <Container paddingX={['12px', '32px']} position="relative" data-test-selector="customers-also-viewed-section" {...(containerProps || {})}>
      {hits?.length > 0 && (
        <Stack spacing="32px" ref={ref}>
          <Flex>
            <h2
              style={{
                width: '100%',
                textAlign: 'center',
                borderBottom: '1px solid #d8d8d8',
                lineHeight: '0px',
                margin: '20px 0px',
              }}
            >
              <span className={styles.recommenderTitle}>{name}</span>
            </h2>
          </Flex>
          <Stack gap={['20px', '28px']} position="relative">
            <Carousel
              loop={false}
              className="recommendations-carousel"
              itemClassName={recommenderItemSpacing}
              controlsPosition="bottom"
              controlConfig={{
                activeDotColor: 'gray800',
                showArrows: {mobile: false, desktop: true, tablet: true},
                arrowsColor: 'gray800',
              }}
              customResponsiveConfig={{
                desktop: {
                  breakpoint: {min: 1024, max: 99999},
                  items: 5,
                  slidesToSlide: 5,
                },
                tablet: {
                  breakpoint: {max: 1024, min: 780},
                  items: 4,
                  slidesToSlide: 4,
                },
                mobile: {
                  breakpoint: {max: 780, min: 480},
                  items: 2,
                  slidesToSlide: 2,
                },
                'mobile-xs': {
                  breakpoint: {max: 480, min: 0},
                  items: 1,
                  slidesToSlide: 1,
                  partialVisibilityGutter: 50,
                },
              }}
            >
              {hits.map((hit, hitItemIndex) => {
                return (
                  hit && (
                    <ProductTile
                      einsteinRecoParams={{recoUUID: recoUUID ?? '', recommender: (recommender || '')}}
                      hit={hit as unknown as Hit}
                      key={hit.id}
                      className={recommenderHit}
                      isPLPTile={true}
                      itemListName={ANALYTICS_CAROUSEL_NAME}
                      itemListId='Einstein'
                      hitItemIndex={hitItemIndex}
                    />
                  )
                )
              })}
            </Carousel>
          </Stack>
        </Stack>
      )}
      {loading && (
        <Flex justify="center" align="center" style={{minHeight: '200px'}}>
          <Spinner color="primary" />
        </Flex>
      )}
    </Container>
  )
}

export const Recommender = observer(RecommenderContainer)
